// Clavian Boot's server state, as TanStack Query hooks over the generated client — `packages/contracts/src/routes/boot.ts`:
//
//   GET   …/boot/servers  images  pcs  metrics      boot:view
//   POST  …/boot/servers/pairing-code                boot:manage      PATCH …/servers/{id}   POST …/servers/{id}/unpair
//   POST  …/boot/servers/{id}/update                 boot:manage      install the server's own downloaded update now
//   POST  …/boot/images   PATCH …/images/{id}        boot:manage      …/restore  …/merge
//   POST  …/boot/images/{id}/edits                   boot:edit_image  …/edits/{id}/publish  …/discard
//   POST  …/boot/images/{id}/server-edits            boot:edit_image  update a game disk on the boot server itself
//   GET   …/boot/floor                             boot:view        each PC on the floor with its boot state
//   GET   …/boot/floor-states                      floor:read       the same state and reason code, and nothing else
//   GET   …/boot/classes  PUT …/classes/{id}       boot:view; boot:manage + stations:manage   what a class's PCs boot
//   POST  …/boot/images/{id}/make-independent      boot:manage      an image saved from another stands on its own
//   GET   …/boot/new-pcs                           boot:view        the Floor's New PCs tray
//   PATCH …/boot/pcs/{id}  POST …/pcs/{id}/remove  POST …/rollouts/restart-idle   boot:manage
//   POST  …/boot/pcs/{id}/identify                 boot:manage      show a number on the PC's own screen
//   POST  …/boot/pcs/{id}/make-station             boot:manage + stations:manage   a new PC becomes a station
//   POST  …/boot/wipes                               boot:wipe
//
// Every route needs the organisation's `boot` add-on; without it each answers `402`, which `isNotOnPlan` reads so the
// screen can say so kindly. While the live socket is up the lists are patched from `venue:{v}:boot` (`realtime.ts`); until
// it is, they refetch on an interval.

import type {
  BootMetrics,
  CreateBootImage,
  IdempotencyKey,
  MakeBootPcStation,
  CreateBootServerPairingCode,
  CreateBootWipe,
  UpdateBootClass,
  UpdateBootImage,
  UpdateBootPc,
} from '@clavian/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '../api/client';
import { problemStatus } from '../api/problems';
import { floorKeys } from '../floor/queries';

export const bootKeys = {
  all: (organizationId: string, venueId: string) => ['boot', organizationId, venueId] as const,
  servers: (organizationId: string, venueId: string) => ['boot', organizationId, venueId, 'servers'] as const,
  images: (organizationId: string, venueId: string) => ['boot', organizationId, venueId, 'images'] as const,
  versions: (organizationId: string, venueId: string, imageId: string) => ['boot', organizationId, venueId, 'versions', imageId] as const,
  pcs: (organizationId: string, venueId: string) => ['boot', organizationId, venueId, 'pcs'] as const,
  /** The boot state of each PC on the floor (`GET …/boot/floor`), joined to the floor's tiles by station. */
  floor: (organizationId: string, venueId: string) => ['boot', organizationId, venueId, 'floor'] as const,
  /** The simple boot state of each PC on the floor (`GET …/boot/floor-states`), for a member without `boot:view`. */
  floorStates: (organizationId: string, venueId: string) => ['boot', organizationId, venueId, 'floor-states'] as const,
  /** What each station class's PCs boot (`GET …/boot/classes`). */
  classes: (organizationId: string, venueId: string) => ['boot', organizationId, venueId, 'classes'] as const,
  /** The PCs that start from the boot server and are not a station yet (`GET …/boot/new-pcs`): the Floor's New PCs tray. */
  newPcs: (organizationId: string, venueId: string) => ['boot', organizationId, venueId, 'new-pcs'] as const,
  /** The latest `boot.job.progress` per version, written by the live socket (`realtime.ts`); never fetched. */
  jobs: (organizationId: string, venueId: string) => ['boot', organizationId, venueId, 'jobs'] as const,
  metrics: (organizationId: string, venueId: string, from: string, to: string, pc: string | null) =>
    ['boot', organizationId, venueId, 'metrics', from, to, pc] as const,
};

/** The api answers `402` to every boot route of an organisation without the Clavian Boot add-on. */
export function isNotOnPlan(error: unknown): boolean {
  return problemStatus(error) === 402;
}

/** How often a list is read while the live socket is not carrying it. */
export const BOOT_POLL_MS = 15_000;

export interface BootScope {
  readonly organizationId: string;
  readonly venueId: string;
}

const retryUnlessRefused = (count: number, error: unknown): boolean => !isNotOnPlan(error) && problemStatus(error) !== 403 && count < 2;

/** The venue's boot server (a venue has one), once the member may read boot and the organisation has the add-on. */
export function useBootServers({ organizationId, venueId }: BootScope, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: bootKeys.servers(organizationId, venueId),
    queryFn: async () => (await api.listBootServers({ path: { organizationId, venueId } })).servers,
    enabled,
    retry: retryUnlessRefused,
    refetchInterval: BOOT_POLL_MS,
  });
}

export function useBootImages({ organizationId, venueId }: BootScope, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: bootKeys.images(organizationId, venueId),
    queryFn: async () => (await api.listBootImages({ path: { organizationId, venueId } })).images,
    enabled,
    retry: retryUnlessRefused,
    refetchInterval: BOOT_POLL_MS,
  });
}

/** One image's versions, newest first — its restore points, and the ones being made. */
export function useBootVersions({ organizationId, venueId }: BootScope, imageId: string | undefined, poll = false) {
  const api = useApi();
  return useQuery({
    queryKey: bootKeys.versions(organizationId, venueId, imageId ?? ''),
    queryFn: async () => (await api.listBootImageVersions({ path: { organizationId, venueId, imageId: imageId ?? '' } })).versions,
    enabled: imageId !== undefined,
    // A wizard watching a capture or a preparation reads again until the live socket has said it is done.
    refetchInterval: poll ? 4_000 : false,
  });
}

export function useBootPcs({ organizationId, venueId }: BootScope, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: bootKeys.pcs(organizationId, venueId),
    queryFn: async () => (await api.listBootPcs({ path: { organizationId, venueId } })).pcs,
    enabled,
    retry: retryUnlessRefused,
    refetchInterval: BOOT_POLL_MS,
  });
}

/**
 * The boot state of every PC linked to a station, for the Floor's tiles and the station window. Read only where the member
 * may view boot; an organisation without the add-on answers `402` once and is not asked again.
 */
export function useBootFloor({ organizationId, venueId }: BootScope, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: bootKeys.floor(organizationId, venueId),
    queryFn: async () => (await api.getBootFloor({ path: { organizationId, venueId } })).pcs,
    enabled,
    retry: retryUnlessRefused,
    refetchInterval: (query) => (query.state.error !== null && (isNotOnPlan(query.state.error) || problemStatus(query.state.error) === 403) ? false : BOOT_POLL_MS),
  });
}

/**
 * The simple boot state of each PC on the floor — the state and why it cannot start, nothing more — for a member who sees
 * the Floor without `boot:view` (a cashier). Read again on `floor.boot.changed` (`floor/realtime.ts`); polled otherwise.
 */
export function useBootFloorStates({ organizationId, venueId }: BootScope, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: bootKeys.floorStates(organizationId, venueId),
    queryFn: async () => (await api.getBootFloorStates({ path: { organizationId, venueId } })).pcs,
    enabled,
    retry: retryUnlessRefused,
    refetchInterval: (query) => (query.state.error !== null && (isNotOnPlan(query.state.error) || problemStatus(query.state.error) === 403) ? false : BOOT_POLL_MS * 2),
  });
}

/** What each station class's PCs boot: its Windows image and game disks, null where not set. */
export function useBootClasses({ organizationId, venueId }: BootScope, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: bootKeys.classes(organizationId, venueId),
    queryFn: async () => (await api.listBootClasses({ path: { organizationId, venueId } })).classes,
    enabled,
    retry: retryUnlessRefused,
  });
}

/** Set what a class's PCs boot. The Floor's boot states are read again (its PCs may now have an update waiting). */
export function useUpdateBootClass(scope: BootScope) {
  const api = useApi();
  return useBootMutation(scope, (variables: { readonly classId: string; readonly body: UpdateBootClass }) =>
    api.updateBootClass({ path: { ...scope, classId: variables.classId }, body: variables.body }),
  );
}

/** Make an image saved from another independent: the boot server flattens its first version into a base of its own. */
export function useMakeBootImageIndependent(scope: BootScope) {
  const api = useApi();
  return useBootMutation(scope, (imageId: string) => api.makeBootImageIndependent({ path: { ...scope, imageId } }));
}

/** The New PCs tray's read: a live PC with no station, newest first. Disabled without `boot:view`; a `402` is not retried. */
export function useBootNewPcs({ organizationId, venueId }: BootScope, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: bootKeys.newPcs(organizationId, venueId),
    queryFn: async () => (await api.listBootNewPcs({ path: { organizationId, venueId } })).pcs,
    enabled,
    retry: retryUnlessRefused,
    refetchInterval: (query) => (query.state.error !== null && (isNotOnPlan(query.state.error) || problemStatus(query.state.error) === 403) ? false : BOOT_POLL_MS),
  });
}

/** Health over a range, given as two instants so one read is one key. */
export function useBootMetrics({ organizationId, venueId }: BootScope, range: { readonly from: string; readonly to: string }, pc: string | null = null) {
  const api = useApi();
  return useQuery<BootMetrics>({
    queryKey: bootKeys.metrics(organizationId, venueId, range.from, range.to, pc),
    queryFn: () => api.getBootMetrics({ path: { organizationId, venueId }, query: { from: range.from, to: range.to, ...(pc === null ? {} : { pc }) } }),
    refetchInterval: 60_000,
  });
}

/** Run a mutation, then read the boot lists again (the realtime frame usually has already). */
function useBootMutation<Variables, Result>(scope: BootScope, run: (variables: Variables) => Promise<Result>) {
  const queryClient = useQueryClient();
  return useMutation<Result, unknown, Variables>({
    mutationFn: run,
    onSettled: () => queryClient.invalidateQueries({ queryKey: bootKeys.all(scope.organizationId, scope.venueId) }),
  });
}

export function useCreatePairingCode(scope: BootScope) {
  const api = useApi();
  return useBootMutation(scope, (body: CreateBootServerPairingCode) => api.createBootServerPairingCode({ path: { ...scope }, body }));
}

export function useRenameBootServer(scope: BootScope) {
  const api = useApi();
  return useBootMutation(scope, (variables: { readonly bootServerId: string; readonly name: string }) =>
    api.updateBootServer({ path: { ...scope, bootServerId: variables.bootServerId }, body: { name: variables.name } }),
  );
}

export function useUnpairBootServer(scope: BootScope) {
  const api = useApi();
  return useBootMutation(scope, (bootServerId: string) => api.unpairBootServer({ path: { ...scope, bootServerId } }));
}

/** Update now: the boot server installs the update it downloaded and verified (`force`: even with PCs booted from it). */
export function useUpdateBootServerSoftware(scope: BootScope) {
  const api = useApi();
  return useBootMutation(scope, (variables: { readonly bootServerId: string; readonly force: boolean }) =>
    api.updateBootServerSoftware({ path: { ...scope, bootServerId: variables.bootServerId }, body: { force: variables.force } }),
  );
}

export function useCreateBootImage(scope: BootScope) {
  const api = useApi();
  return useBootMutation(scope, (body: CreateBootImage) => api.createBootImage({ path: { ...scope }, body }));
}

export function useUpdateBootImage(scope: BootScope) {
  const api = useApi();
  return useBootMutation(scope, (variables: { readonly imageId: string; readonly body: UpdateBootImage }) =>
    api.updateBootImage({ path: { ...scope, imageId: variables.imageId }, body: variables.body }),
  );
}

export function useRestoreBootImage(scope: BootScope) {
  const api = useApi();
  return useBootMutation(scope, (variables: { readonly imageId: string; readonly versionId: string }) =>
    api.restoreBootImage({ path: { ...scope, imageId: variables.imageId }, body: { versionId: variables.versionId } }),
  );
}

/** Prepare a failed captured or imported version again, on the layer the boot server already holds. */
export function usePrepareBootVersionAgain(scope: BootScope) {
  const api = useApi();
  return useBootMutation(scope, (variables: { readonly imageId: string; readonly versionId: string }) =>
    api.prepareBootImageVersion({ path: { ...scope, imageId: variables.imageId }, body: { versionId: variables.versionId } }),
  );
}

export function useMergeBootImage(scope: BootScope) {
  const api = useApi();
  return useBootMutation(scope, (variables: { readonly imageId: string; readonly upToVersionId: string }) =>
    api.mergeBootImage({ path: { ...scope, imageId: variables.imageId }, body: { upToVersionId: variables.upToVersionId } }),
  );
}

export function useStartBootEdit(scope: BootScope) {
  const api = useApi();
  return useBootMutation(scope, (variables: { readonly imageId: string; readonly bootPcId: string }) =>
    api.startBootEdit({ path: { ...scope, imageId: variables.imageId }, body: { bootPcId: variables.bootPcId } }),
  );
}

/** Update on the server: the boot server opens a new version of the game disk on its drive letter. */
export function useStartBootServerEdit(scope: BootScope) {
  const api = useApi();
  return useBootMutation(scope, (imageId: string) => api.startBootServerEdit({ path: { ...scope, imageId } }));
}

/** Publish an edit: a new version of its image, or — `asNewImage` — version 1 of a new image standing on it. */
export function usePublishBootEdit(scope: BootScope) {
  const api = useApi();
  return useBootMutation(scope, (variables: { readonly editId: string; readonly note: string; readonly asNewImage?: { readonly name: string } }) =>
    api.publishBootEdit({
      path: { ...scope, editId: variables.editId },
      body: { note: variables.note, ...(variables.asNewImage === undefined ? {} : { asNewImage: { name: variables.asNewImage.name } }) },
    }),
  );
}

export function useDiscardBootEdit(scope: BootScope) {
  const api = useApi();
  return useBootMutation(scope, (editId: string) => api.discardBootEdit({ path: { ...scope, editId } }));
}

export function useUpdateBootPc(scope: BootScope) {
  const api = useApi();
  return useBootMutation(scope, (variables: { readonly bootPcId: string; readonly body: UpdateBootPc }) =>
    api.updateBootPc({ path: { ...scope, bootPcId: variables.bootPcId }, body: variables.body }),
  );
}

export function useWipeBoot(scope: BootScope) {
  const api = useApi();
  return useBootMutation(scope, (body: CreateBootWipe) => api.createBootWipe({ path: { ...scope }, body }));
}

export function usePromoteBootShaderSeed(scope: BootScope) {
  const api = useApi();
  return useBootMutation(scope, (bootPcId: string) => api.promoteBootShaderSeed({ path: { ...scope, bootPcId } }));
}

/** Restart the PCs behind on an image when free: all of them, or only `bootPcIds` (the Floor's per-PC and filter actions). */
export function useRestartIdleBootPcs(scope: BootScope) {
  const api = useApi();
  return useBootMutation(scope, (variables: string | { readonly imageId: string; readonly bootPcIds?: readonly string[] }) => {
    const { imageId, bootPcIds } = typeof variables === 'string' ? { imageId: variables, bootPcIds: undefined } : variables;
    return api.restartIdleBootPcs({ path: { ...scope }, body: { imageId, ...(bootPcIds === undefined || bootPcIds.length === 0 ? {} : { bootPcIds: [...bootPcIds] }) } });
  });
}

export function useRemoveBootPc(scope: BootScope) {
  const api = useApi();
  return useBootMutation(scope, (bootPcId: string) => api.removeBootPc({ path: { ...scope, bootPcId } }));
}

/** Identify: the PC shows `label` (the number it would be given) on its own screen for a minute. */
export function useIdentifyBootPc(scope: BootScope) {
  const api = useApi();
  return useBootMutation(scope, (variables: { readonly bootPcId: string; readonly label: string }) =>
    api.identifyBootPc({ path: { ...scope, bootPcId: variables.bootPcId }, body: { label: variables.label } }),
  );
}

/**
 * Make it station: a new PC becomes a station and its boot PC is linked to it, in one request. A fresh `Idempotency-Key` per
 * submit (kept by the caller across a retry of the same form), so a double-click makes one station. The Floor and the Stations
 * list are read again.
 */
export function useMakeBootPcStation(scope: BootScope) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: { readonly bootPcId: string; readonly body: MakeBootPcStation; readonly idempotencyKey: string }) =>
      api.makeBootPcStation({
        path: { ...scope, bootPcId: variables.bootPcId },
        headerParams: { 'idempotency-key': variables.idempotencyKey as IdempotencyKey },
        body: variables.body,
      }),
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: bootKeys.all(scope.organizationId, scope.venueId) }),
        queryClient.invalidateQueries({ queryKey: floorKeys.snapshot(scope.organizationId, scope.venueId) }),
        queryClient.invalidateQueries({ queryKey: ['stations', scope.organizationId, scope.venueId] }),
      ]),
  });
}
